import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";
import axios, { SERVER_URL } from "../../utils/axios";
import { addQboOrg } from "../../utils/organization";
import { checkAuthentication } from "../../utils/users";
import { isEmailVerifiedClaim } from "../../utils/auth";

/**
 * Intuit App Store "Connect / Reconnect" (Production Connect Request) URL.
 *
 * When a user clicks "Get App Now" on Apps.com / Intuit App Store, Intuit
 * redirects here without a prior Wowzer session. Per Intuit App Review
 * requirements (4.1 Get app now):
 *   1. The OAuth flow MUST be triggered first (no local sign-in page interruption).
 *   2. The combined scopes (OpenID Connect + Accounting) are requested.
 *   3. If the user account doesn't exist, it is created automatically on the fly
 *      during OAuth callback.
 *   4. The user is redirected inside the application without being asked to log in again.
 */
const IntuitConnect = () => {
  const { isAuthenticated, isLoading, getIdTokenClaims } = useAuth0();
  const navigate = useNavigate();
  const hasStarted = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isLoading || hasStarted.current) return;
    hasStarted.current = true;

    // If user is not authenticated, immediately trigger the Intuit OAuth flow directly
    if (!isAuthenticated) {
      const apiBase = axios.defaults.baseURL || SERVER_URL || "";
      window.location.href = `${apiBase}/connect/intuit-appcenter`;
      return;
    }

    // If user is already authenticated in Wowzer, proceed with connecting the QBO account
    (async () => {
      try {
        const claims = await getIdTokenClaims();
        const token = claims?.__raw;
        if (!token) {
          setError("Authentication token not available. Please try again.");
          return;
        }

        if (isEmailVerifiedClaim(claims, null) === false) {
          navigate("/intuit-unverified", { replace: true });
          return;
        }

        const authRes = await checkAuthentication(token);
        if (
          authRes?.data?.is_intuit_unverified ||
          authRes?.data?.error === "INTUIT_EMAIL_NOT_VERIFIED"
        ) {
          navigate("/intuit-unverified", { replace: true });
          return;
        }

        addQboOrg(token);
      } catch (e) {
        console.error(e);
        if (
          e?.response?.data?.is_intuit_unverified ||
          e?.response?.data?.error === "INTUIT_EMAIL_NOT_VERIFIED"
        ) {
          navigate("/intuit-unverified", { replace: true });
          return;
        }
        setError(
          e?.response?.data?.message ||
            e?.message ||
            "Failed to start QuickBooks connection. Please try again."
        );
      }
    })();
  }, [isLoading, isAuthenticated, getIdTokenClaims, navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-gray-50 px-4">
      {error ? (
        <p className="max-w-md text-center text-sm text-red-600">{error}</p>
      ) : (
        <Spin size="large" />
      )}
    </div>
  );
};

export default IntuitConnect;
